import { defineStore } from "pinia";
import APIRequest from "@/libs/cloud_auth";
import { useAppStore } from "../app";

export const useAlertStore = defineStore("alert", {
  state: () => ({
    // Notifications State
    items: [],
    count: 0,
    totalPages: 1,

    // Alerts State
    alertsList: [],
    alertsCount: 0,
    alertsTotalPages: 1,

    // Shared Status State
    isLoading: false,
    error: "",
    controller: null,
  }),
  getters: {},
  actions: {
    calculateTotalPages(count, pageSize) {
      return Math.ceil(count / pageSize) || 1;
    },
    async getNotificationsRaw(){
      return new Promise((resolve, reject) => {
        APIRequest(`notifications/?page_size=10`, "get")
          .then((response) => {
            if (!response?.data) return [];
            resolve(response.data);
          })
          .catch(() => {
            reject([]);
          });
      })
    },
    updateLocalNotification(id, data) {
        const index = this.items.findIndex(item => item.id === id);
        if (index !== -1) {
            this.items[index] = { 
                ...this.items[index], 
                ...data,
                actionTaken: true 
            };
        }
    },
    async getNotifications(search, page, pageSize, range, sort, tenants) {
      if (this.isLoading && this.controller) {
        this.controller.abort();
      }

      this.controller = new AbortController();
      this.isLoading = true;
      this.error = "";

      const queryString = `?page=${page}&page_size=${pageSize}&search=${search}`;
      const selectedTenants = !tenants || !tenants.length ? useAppStore().getListedHotels : tenants 
      
      const payload = {
        tenant_list: selectedTenants,
        range: range,
        ordering: sort
      };

      await APIRequest(`notifications/list_notifications/${queryString}`, "post", payload, {
        signal: this.controller.signal,
      })
        .then((response) => {
          if (!response?.data) return;

          const data = response.data;

          this.count = data.count || 0;
          this.next = data.next;
          this.previous = data.previous;
          this.totalPages = this.calculateTotalPages(this.count, pageSize);

          this.items = (data.results || []).map((n) => ({
            id: n.id, 
            alertId: n.alert_id,
            timestamp: n.created_at,
            severity: n.severity,
            sourceId: n.source ? n.source?.ncm_id : null,
            source: n.source ? n.source?.name : "Unknown",
            name: n.title,
            description: n.description,
            read: n.is_read,
            actionTaken: !!n.acknowledge_user,
            acknowledge_user: n.acknowledge_user,
            acknowledged_on: n.acknowledged_on,

            category: null,
            eventType: null,
            eventCount: null,
            period: null,
            devices: n.devices || [],
          }));

          this.isLoading = false;
        })
        .catch((error) => {
          if (error.name !== "CanceledError") {
            this.error = error;
            console.error("Fetch error:", error);
            this.isLoading = false;
          }
        });
    },
    async getAlerts(search, page, pageSize, sort, tenants) {
      if (this.isLoading && this.controller) {
        this.controller.abort();
      }

      this.controller = new AbortController();
      this.isLoading = true;
      this.error = "";

      const queryString = `?page=${page}&page_size=${pageSize}&search=${search || ''}`;
      const selectedTenants = !tenants || !tenants.length ? useAppStore().getListedHotels : tenants 

      const payload = {
        tenants: selectedTenants,
        ordering: sort
      };

      try {
        const response = await APIRequest(`alerts/list_alerts/${queryString}`, "post", payload, {
          signal: this.controller.signal,
        });

        if (!response?.data) return;

        const data = response.data;

        this.alertsCount = data.count || 0;
        this.alertsTotalPages = this.calculateTotalPages(this.alertsCount, pageSize);
        this.alertsList = data.results || [];

      } catch (error) {
        if (error.name !== "CanceledError") {
          this.error = error;
          console.error("Fetch alerts error:", error);
        }
      } finally {
        this.isLoading = false;
      }
    },
    getAlertById(id) {
      return new Promise((resolve, reject) => {
        APIRequest(`alerts/${id}/`, "get")
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    createAlert(payload) {
      return new Promise((resolve, reject) => {
        APIRequest("alerts/", "post", payload)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    editAlert(payload) {
      return new Promise((resolve, reject) => {
        APIRequest(`alerts/${payload.id}/`, "patch", payload)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    deleteAlert(id) {
      return new Promise((resolve, reject) => {
        APIRequest(`alerts/${id}/`, "delete")
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getEvents() {
      return new Promise((resolve, reject) => {
        APIRequest("events/all/", "get")
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getEventCategories() {
      return new Promise((resolve, reject) => {
        APIRequest("events/categories/", "get")
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getEventSeverities() {
      return new Promise((resolve, reject) => {
        APIRequest("events/severities/", "get")
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    editNotification(id, payload) {
      return new Promise((resolve, reject) => {
        APIRequest(`notifications/${id}/`, "patch", payload)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    readNotification(id) {
      return new Promise((resolve, reject) => {
        APIRequest(`notifications/${id}/`, "patch", {
          is_read: true,
        })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    readAllNotifications() {
      return new Promise((resolve, reject) => {
        APIRequest("notifications/set_all/", "post", {
          action: "read",
        })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
});
